import { Home, WifiOff, AlertTriangle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import Profile from "./profile";
import { useAuth } from "@/hooks/useAuth";
import { setOfflineMode } from "@/redux/slices/offline/offlineSlice";
import { Button } from "@/components/ui/button";

// Add isOffline prop
interface NavbarProps {
  isOffline?: boolean;
}

export default function Navbar({ isOffline = false }: NavbarProps) {
  const isOnline = useSelector((state: RootState) => state.netWork.isOnline);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const location = useLocation();

  // Check if we're in offline mode from the URL
  const isOfflineMode = location.pathname.includes("offline");
  // Inside a project, Home returns to that module's project list (which has its own Back to home).
  const homeModule = location.pathname.startsWith("/distribution-analytics/")
    ? "distribution"
    : location.pathname.startsWith("/projects/")
      ? "test-log"
      : null;
  const homePath = isOfflineMode
    ? "/offline/projects"
    : homeModule
      ? `/?module=${homeModule}`
      : "/";

  // Handle switching to offline mode
  const switchToOfflineMode = () => {
    dispatch(setOfflineMode(true));
  };

  return (
    <nav className="w-full flex-center justify-between px-3 sm:px-5 md:px-10 py-2 bg-blue sticky top-0 z-20 h-[70px]">
      <div className="flex-center divide-x-[1px] divide-[#C3C3C3] gap-2 sm:gap-3 md:gap-5">
        <Link
          to={homePath}
          aria-label={isOfflineMode ? "Back to offline project list" : "Home"}
          className="flex gap-2 items-center rounded-full"
        >
          <img src={'/pepsicoR&D.svg'} alt="Logo" className='size-10' />
          <Home className="text-white" />
        </Link>

        <h2 className="semi-bold-20 text-sm lg:text-base text-white capitalize pl-2 sm:pl-3 md:pl-5">
          {location.pathname === "/" ? "Pepsico R&D" : location.pathname.includes('distribution-analytics')? "Distribution Analytics" : location.pathname.includes('/projects')? "Physical Test Data Collection" : "Pepsico R&D"}
        </h2>
      </div>
      <div className=" bg-white w-[60px] h-[60px] rounded-full flex-center">
        <img
          className="hidden min-[450px]:block"
          width={70}
          height={70}
          src={'/main-pepsico.png'}
          alt="pepsico"
        />
      </div>

      <div className="flex-center gap-4">
        {/* Mode indicator */}
        {isOfflineMode ? (
          <div className="text-white bg-red-500 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <WifiOff size={14} />
            Offline Mode
          </div>
        ) : (
          <>
            {/* Show offline warning and button when network is offline but not in offline mode */}
            {!isOnline && (
              <div className="flex items-center gap-2 ">
                <div className="text-white bg-orange-500 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <AlertTriangle size={14} />
                  You're offline
                </div>
                <Link to="/offline/projects">
                  <Button
                    variant="outline"
                    onClick={switchToOfflineMode}
                    className="text-white border-white bg-blue  hover:text-blue hover:bg-blue text-xs flex items-center gap-1"
                  >
                    <WifiOff size={14} />
                    Switch to Offline Mode
                  </Button>
                </Link>
              </div>
            )}

            {(location.pathname.includes('new-project') || location.pathname.includes('edit-project')) && isOnline && (
              <div className="text-white bg-states-success px-2 py-1 rounded-full text-xs font-medium">
                Online Mode
              </div>
            )}
          </>
        )}

       <div className=" flex items-center gap-1">
       {!isOffline && <Profile
          role="admin"
          email={user?.email}
          displayName={`${user?.firstName} ${user?.lastName}`}
        />}
        <div className=" text-sm font-bold text-white">v.1.4</div>
       </div>
      </div>
    </nav>
  );
}
